<template>
  <div
    class="tk-row"
    :class="{
      'tk-row--done': isDone,
      'tk-row--completing': completing,
      'tk-row--flash': highlight,
    }"
    data-test="tasks-row"
    :data-task-id="task.id"
    @click="open"
  >
    <div class="tk-row__check-cell">
      <button
        type="button"
        class="tk-row__check"
        :class="{ 'is-checked': checked }"
        :disabled="checked"
        :title="checked ? (isDone ? 'Completed' : 'Completing…') : 'Mark complete'"
        :aria-label="checked ? `Completed: ${meta.text || 'task'}` : `Mark complete: ${meta.text || 'task'}`"
        data-test="tasks-row-check"
        @click.stop="complete"
      >
        <svg viewBox="0 0 24 24" width="12" height="12" class="tk-row__svg tk-row__svg--bold" aria-hidden="true" focusable="false">
          <path :d="ICONS.check[0]" />
        </svg>
      </button>
    </div>

    <div class="tk-row__main">
      <div class="tk-row__title-line">
        <button type="button" class="tk-row__title" data-test="tasks-row-open" @click.stop="open">
          {{ meta.text || 'Untitled task' }}
        </button>
        <span v-if="meta.isNewToday && !checked" class="tk-row__new" data-test="tasks-row-new">New today</span>
      </div>

      <div v-if="metaItems.length" class="tk-row__meta" data-test="tasks-row-meta">
        <span
          v-for="m in metaItems"
          :key="m.key"
          class="tk-row__meta-item"
          :class="m.cls"
          :data-test="m.test"
          :title="m.title || null"
        >
          <svg v-if="m.icon" viewBox="0 0 24 24" width="13" height="13" class="tk-row__svg" aria-hidden="true" focusable="false">
            <path v-for="(d, i) in ICONS[m.icon]" :key="i" :d="d" />
          </svg>{{ m.text }}
        </span>
      </div>
    </div>

    <div class="tk-row__side">
      <button
        v-if="movable && !checked"
        type="button"
        class="tk-row__move"
        data-test="tasks-row-move"
        @click.stop="$emit('move', task)"
      >
        <svg viewBox="0 0 24 24" width="13" height="13" class="tk-row__svg" aria-hidden="true" focusable="false">
          <path v-for="(d, i) in ICONS.cal" :key="i" :d="d" />
        </svg>Move to today
      </button>
      <span
        v-if="showDue && dueLabel && !isDone"
        class="tk-row__due"
        :class="{ 'tk-row__due--late': isLate, 'tk-row__due--today': dueDiff === 0 }"
        :title="lateTitle || null"
        data-test="tasks-row-due"
      >{{ dueLabel }}</span>
      <span v-if="people.length" class="tk-row__people">
        <span
          v-for="p in people"
          :key="p.id"
          class="tk-row__avatar"
          :title="p.name"
          data-test="tasks-row-assignee"
        >{{ p.initials }}</span>
        <span v-if="peopleMore" class="tk-row__avatar tk-row__avatar--more" :title="peopleMoreTitle">+{{ peopleMore }}</span>
      </span>
    </div>
  </div>
</template>

<script>
/**
 * TaskRow — one task on the redesigned Tasks page (design "Tasks 2a", Phase B1, 2026-10-06).
 *
 * Plan: C:\Users\nilay\.claude\plans\eager-painting-hejlsberg.md — "Phase B — B1".
 *
 *   <task-row :task="t" :ctx="ctx" :employees="employees" show-due @open="…" @complete="…" />
 *
 * Shows: the round check, the cleaned title (badges pulled out), "New today", and a meta line —
 * tagged patients, "Done by X" (someone else completed it), the task's tags (2 + "+N"), subtasks
 * x/y, comments (only when the task carries them — list tasks do not, see myDay.js rowMeta), an
 * unread-comment marker, attachments (wiki pages) and the repeat rule. On the right: the due day
 * (Today / Tomorrow / weekday / "Sep 22", red when overdue) and the assignees' initials.
 * A COMPLETED task draws struck through with a filled check; there is no completion time to show
 * (tasks/updateTask stamps completedAt at the start of the office day).
 *
 * All the numbers come from src/utils/tasks/myDay.js `rowMeta(task, ctx)`.
 *
 * The check COMPLETES the task with the Tasks list's own dispatches — the same orchestration as
 * src/components/Followups/AdminTaskRow.vue (TodoItem.changeStatus + handleItemChange), built from
 * the shared src/utils/tasks/completeTask.js builders (pinned to TodoItem.vue by
 * cypress/node-tests/tasks-complete-task.test.js): a repeating task due today or later gets its
 * next instance (tasks/addPersonalTask) unless it already exists, then tasks/updateTask saves the
 * completed task. Like TodoItem, the update waits `completeDelay` ms so the tick is seen. Emits
 * `complete(change)` after the update, `open(task)` on a click anywhere else on the row, and
 * `move(task)` from the overdue tray's "Move to today" (`movable`).
 *
 * Never reads $route. No prop named patNum / siteID / loading (the mixin would shadow it).
 */
import moment from 'moment-timezone';
import { rowMeta } from '@/utils/tasks/myDay.js';
import {
  cleanTaskContent, combineStoreTasks, needsNextInstance, findNextInstance, nextInstancePayload, completedTaskPayload,
} from '@/utils/tasks/completeTask.js';

const SAVE_ERROR_TEXT = 'The task could not be completed. Please try again in a moment.';
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// Lucide shapes (path data only), 24×24, stroked.
const ICONS = {
  check: ['M20 6 9 17l-5-5'],
  user: ['M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2', 'M12 3a4 4 0 1 0 0 8a4 4 0 1 0 0-8'],
  tag: [
    'M12.586 2.586A2 2 0 0 0 11.172 2H4a2 2 0 0 0-2 2v7.172a2 2 0 0 0 .586 1.414l8.704 8.704a2.426 2.426 0 0 0 3.42 0l6.58-6.58a2.426 2.426 0 0 0 0-3.42z',
    'M7.5 7.5h.01',
  ],
  subs: ['m3 17 2 2 4-4', 'm3 7 2 2 4-4', 'M13 6h8', 'M13 12h8', 'M13 18h8'],
  msg: ['M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z'],
  clip: ['m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l8.57-8.57A4 4 0 1 1 18 8.84l-8.59 8.57a2 2 0 0 1-2.83-2.83l8.49-8.48'],
  repeat: ['m17 2 4 4-4 4', 'M3 11v-1a4 4 0 0 1 4-4h14', 'm7 22-4-4 4-4', 'M21 13v1a4 4 0 0 1-4 4H3'],
  cal: ['M8 2v4', 'M16 2v4', 'M3 10h18', 'M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z'],
};

const YMD = /^(\d{4})-(\d{2})-(\d{2})$/;
const utcOf = (ymd) => {
  const m = YMD.exec(ymd || '');
  return m ? Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null;
};

/** Whole days from `a` to `b` ('YYYY-MM-DD'), or null. */
function dayDiff(a, b) {
  const x = utcOf(a);
  const y = utcOf(b);
  return x === null || y === null ? null : Math.round((y - x) / 86400000);
}

/** The design's due label: Today · Tomorrow · Wed (within the week) · "Sep 22" (· year when not this year). */
function dueLabelOf(dueDay, today) {
  const n = dayDiff(today, dueDay);
  if (n === null) return '';
  if (n === 0) return 'Today';
  if (n === 1) return 'Tomorrow';
  const d = new Date(utcOf(dueDay));
  if (n > 1 && n < 7) return WEEKDAYS[d.getUTCDay()];
  const label = `${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}`;
  return String(d.getUTCFullYear()) === String(today).slice(0, 4) ? label : `${label}, ${d.getUTCFullYear()}`;
}

/** "Maya Rivera" → "MR". */
function nameInitials(name) {
  const words = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (!words.length) return '';
  const first = words[0][0] || '';
  const last = words.length > 1 ? (words[words.length - 1][0] || '') : '';
  return (first + last).toUpperCase();
}

const firstName = name => String(name || '').trim().split(/\s+/)[0] || '';

export default {
  name: 'TaskRow',
  props: {
    task: { type: Object, required: true },
    // myDay.js ctx: { userID, today: 'YYYY-MM-DD', timeZone, clean? }.
    ctx: { type: Object, required: true },
    // state.customer.employees (EmployeeID, Name) — for initials and "Done by".
    employees: { type: Array, default: () => [] },
    // The due day on the right (Coming up, the overdue tray, the list views).
    showDue: { type: Boolean, default: false },
    // Leave the user out of the assignee initials (the "mine" views: they are all theirs).
    hideSelf: { type: Boolean, default: false },
    // The overdue tray's per-row "Move to today" (non-repeating tasks only — the page decides).
    movable: { type: Boolean, default: false },
    // Briefly tint the row (a task just added from the composer).
    highlight: { type: Boolean, default: false },
    // TodoItem's 1200ms between the tick and the save. Specs pass 0.
    completeDelay: { type: Number, default: 1200 },
  },

  data() {
    return { completing: false, ICONS };
  },

  computed: {
    meta() {
      return rowMeta(this.task, this.ctx);
    },
    isDone() {
      return this.task.status === 'COMPLETED';
    },
    checked() {
      return this.isDone || this.completing;
    },
    meID() {
      return this.ctx && this.ctx.userID != null ? String(this.ctx.userID) : null;
    },
    dueDiff() {
      return this.meta.dueDay ? dayDiff(this.ctx.today, this.meta.dueDay) : null;
    },
    isLate() {
      return this.dueDiff !== null && this.dueDiff < 0 && !this.isDone;
    },
    dueLabel() {
      return this.meta.dueDay ? dueLabelOf(this.meta.dueDay, this.ctx.today) : '';
    },
    lateTitle() {
      if (!this.isLate) return '';
      const n = -this.dueDiff;
      return n === 1 ? '1 day late' : `${n} days late`;
    },
    metaItems() {
      const m = this.meta;
      const out = [];
      (m.patients || []).forEach((p, i) => {
        if (p && p.name) out.push({ key: `p${i}`, icon: 'user', text: p.name, cls: 'tk-row__meta-item--patient', test: 'tasks-row-patient' });
      });
      if (this.isDone && m.completedByEmpID != null && String(m.completedByEmpID) !== this.meID) {
        const who = firstName(this.employeeName(m.completedByEmpID));
        if (who) out.push({ key: 'by', icon: 'check', text: `Done by ${who}`, test: 'tasks-row-done-by' });
      }
      const tags = m.tags || [];
      tags.slice(0, 2).forEach((g, i) => out.push({ key: `t${i}`, icon: 'tag', text: g, test: 'tasks-row-tag' }));
      if (tags.length > 2) out.push({ key: 'tmore', text: `+${tags.length - 2}`, title: tags.slice(2).join(', '), test: 'tasks-row-tag-more' });
      if (m.subtasks) out.push({ key: 'sub', icon: 'subs', text: `${m.subtasks.done}/${m.subtasks.total}`, test: 'tasks-row-subtasks' });
      if (typeof m.comments === 'number' && m.comments > 0) {
        out.push({
          key: 'com',
          icon: 'msg',
          text: m.unreadComments ? `${m.comments} new` : String(m.comments),
          cls: m.unreadComments ? 'tk-row__meta-item--unread' : '',
          test: 'tasks-row-comments',
        });
      } else if (m.unreadComments) {
        out.push({ key: 'com', icon: 'msg', text: 'New comment', cls: 'tk-row__meta-item--unread', test: 'tasks-row-unread' });
      }
      if (m.attachments > 0) out.push({ key: 'att', icon: 'clip', text: String(m.attachments), test: 'tasks-row-attachments' });
      if (m.repeatLabel) out.push({ key: 'rep', icon: 'repeat', text: m.repeatLabel, test: 'tasks-row-repeat' });
      return out;
    },
    peopleAll() {
      const ids = (this.meta.assigneeEmpIDs || []).map(String);
      return this.hideSelf ? ids.filter(id => id !== this.meID) : ids;
    },
    people() {
      return this.peopleAll.slice(0, 2).map((id) => {
        const name = this.employeeName(id);
        return { id, name: name || 'Unknown', initials: nameInitials(name) || '?' };
      });
    },
    peopleMore() {
      return Math.max(0, this.peopleAll.length - 2);
    },
    peopleMoreTitle() {
      return this.peopleAll.slice(2).map(id => this.employeeName(id) || 'Unknown').join(', ');
    },
    storeTasks() {
      const g = (this.$store && this.$store.getters) || {};
      // ctx.knownTasks (optional): extra tasks the host already holds — the patient Tasks tab passes the
      // patient's tasks, so the next-instance check works before the Tasks page's lists have ever loaded.
      const known = this.ctx && Array.isArray(this.ctx.knownTasks) ? this.ctx.knownTasks : [];
      return combineStoreTasks(
        combineStoreTasks(g['tasks/myCreatedTasks'], g['tasks/assignedToMeTasks'], g['tasks/myEmployeesTasks']),
        known,
      );
    },
    currentUserId() {
      return this.ctx ? this.ctx.userID : null;
    },
  },

  methods: {
    employeeName(id) {
      const e = (this.employees || []).find(x => x && String(x.EmployeeID) === String(id));
      return e ? e.Name : '';
    },

    open() {
      this.$emit('open', this.task);
    },

    textOf(content) {
      return cleanTaskContent(content, typeof document !== 'undefined' ? document : null).text;
    },

    wait(ms) {
      return ms > 0 ? new Promise(resolve => setTimeout(resolve, ms)) : Promise.resolve();
    },

    // AdminTaskRow.complete, line for line (both follow TodoItem.changeStatus + handleItemChange).
    async complete() {
      const item = this.task;
      if (this.completing || !item || item.status !== 'INCOMPLETE') return;
      this.completing = true;
      try {
        const dueAt = moment(item.dueAt).toDate();
        const dueAtStr = moment(item.dueAt).format('MM/DD/YYYY');
        const completedAt = moment().toDate();
        const completedAtStr = moment().format('MM/DD/YYYY');
        const diff = moment(item.dueAtStr).diff(moment(), 'days');

        if (needsNextInstance(item, diff)) {
          const nextDueAt = this.$store.getters['tasks/getNextDueDate'](item).toDate();
          const nextDueAtStr = moment(nextDueAt).format('MM/DD/YYYY');
          const existing = findNextInstance(item, this.storeTasks, nextDueAtStr, this.textOf);
          if (!existing) {
            await this.$store.dispatch('tasks/addPersonalTask', nextInstancePayload(item, {
              createdAt: moment().toDate(),
              nextDueAt,
              nextDueAtStr,
            }));
          }
        }

        const change = completedTaskPayload(item, {
          dueAt,
          dueAtStr,
          createdAt: moment(item.createdAt).toDate(),
          completedAt,
          completedAtStr,
          completedByEmpID: this.currentUserId,
        });
        await this.wait(this.completeDelay);
        await this.$store.dispatch('tasks/updateTask', change);
        this.$emit('complete', change);
      } catch (err) {
        console.error('[TaskRow] completing the task failed:', err);
        this.completing = false;
        if (this.$toast) this.$toast.error(SAVE_ERROR_TEXT);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../../../components/Followups/mixins';

.tk-row {
  display: flex;
  align-items: flex-start;
  gap: $fu-space-3;
  padding: 11px 12px;
  border-radius: $fu-radius-md;
  min-width: 0;
  cursor: pointer;
  transition: background $fu-dur-fast $fu-ease-out;
  &:hover { background: $fu-neutral-50; }
}

.tk-row--flash { background: $fu-brand-50; }

.tk-row__check-cell {
  flex: none;
  padding-top: 1px;
}

.tk-row__check {
  @include fu-button-reset;
  @include fu-focus-ring;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 1.5px solid $fu-neutral-300;
  background: $fu-surface;
  color: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background $fu-dur-fast $fu-ease-out, border-color $fu-dur-fast $fu-ease-out;
  &:hover:not([disabled]) { border-color: $fu-brand; color: $fu-brand; }
  &.is-checked {
    border-color: $fu-brand;
    background: $fu-brand;
    color: #ffffff;
    cursor: default;
  }
}

.tk-row__svg {
  fill: none;
  stroke: currentColor;
  stroke-width: 2;
  stroke-linecap: round;
  stroke-linejoin: round;
  flex: none;
}

.tk-row__svg--bold { stroke-width: 3; }

.tk-row__main {
  flex: 1;
  min-width: 0;
}

.tk-row__title-line {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px $fu-space-2;
  min-width: 0;
}

.tk-row__title {
  @include fu-button-reset;
  @include fu-focus-ring;
  min-width: 0;
  max-width: 100%;
  border-radius: $fu-radius-xs;
  text-align: left;
  font-size: 15px;
  line-height: 1.35;
  font-weight: $fu-fw-medium;
  color: $fu-text;
  overflow-wrap: anywhere;
}

.tk-row--done .tk-row__title,
.tk-row--completing .tk-row__title {
  color: $fu-text-subtle;
  text-decoration: line-through;
}

.tk-row__new {
  @include fu-pill($fu-brand-50, $fu-brand-700);
  font-weight: $fu-fw-semibold;
}

.tk-row__meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px 14px;
  margin-top: 5px;
  min-width: 0;
  font-size: 12.5px;
  color: $fu-text-muted;
}

.tk-row__meta-item {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  min-width: 0;
  max-width: 100%;
  overflow-wrap: anywhere;
}

.tk-row__meta-item--patient {
  color: $fu-brand-700;
  font-weight: $fu-fw-semibold;
}

.tk-row__meta-item--unread {
  color: $fu-brand;
  font-weight: $fu-fw-bold;
}

.tk-row--done .tk-row__meta-item--patient { color: $fu-text-muted; font-weight: $fu-fw-medium; }

.tk-row__side {
  flex: none;
  display: flex;
  align-items: center;
  gap: $fu-space-3;
  padding-top: 1px;
  max-width: 45%;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.tk-row__move {
  @include fu-button-reset;
  @include fu-focus-ring;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 8px;
  border-radius: $fu-radius-sm;
  font-size: 12.5px;
  font-weight: $fu-fw-semibold;
  color: $fu-brand-700;
  white-space: nowrap;
  &:hover { background: $fu-brand-50; }
}

.tk-row__due {
  font-size: 13px;
  font-weight: $fu-fw-semibold;
  color: $fu-neutral-600;
  white-space: nowrap;
  @include fu-tabular;
}

.tk-row__due--today { color: $fu-text; font-weight: $fu-fw-bold; }
.tk-row__due--late { color: $fu-danger-text; }

.tk-row__people {
  display: inline-flex;
  align-items: center;
}

.tk-row__avatar {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: $fu-neutral-100;
  color: $fu-neutral-600;
  border: 2px solid $fu-surface;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 9px;
  font-weight: $fu-fw-bold;
  & + & { margin-left: -7px; }
}

.tk-row__avatar--more { background: $fu-neutral-50; }

@include fu-phone {
  .tk-row { padding: 11px 6px; }
  .tk-row__side { max-width: 40%; }
}
</style>
